import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { HeaderV2, FooterV2 } from '../../components/layout';
import BasicInformationStepV2 from './CustomizedSteps/BasicInformationStepV2';
import ServicesStepV2 from './CustomizedSteps/ServicesStepV2';
import StayDurationStepV2 from './CustomizedSteps/StayDurationStepV2';
import TransportationContactStepV2 from './CustomizedSteps/TransportationContactStepV2';
import PriceSummaryStepV2 from './CustomizedSteps/PriceSummaryStepV2';
import { ArrowBack, ArrowForward, Check } from '@mui/icons-material';
import { buildCustomizedBookingPayload } from '../../utils/buildCustomizedBookingPayload';
import { API_BASE_URL } from '../../config/api';
import { persistSession } from '../../store/slices/authSlice';
import { coerceBookingLocations, resolveProductLine } from '../../utils/productLineConfig';

const steps = [
  'Basic Information',
  'Services',
  'Destinations & Duration',
  'Transportation & Contact',
  'Price Summary',
];

const CustomizedBookingPageV2 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  const [activeStep, setActiveStep] = useState(() => {
    try {
      const step = sessionStorage.getItem('customizeBookingStep');
      return step != null ? Number(step) || 0 : 0;
    } catch {
      return 0;
    }
  });
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priceCalculation, setPriceCalculation] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showGuestAccountForm, setShowGuestAccountForm] = useState(false);
  const [guestPassword, setGuestPassword] = useState('');
  const [guestPasswordConfirm, setGuestPasswordConfirm] = useState('');
  const [guestAccountLoading, setGuestAccountLoading] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState(null);

  // Form state
  const [bookingData, setBookingData] = useState(() => {
    const defaults = {
      journeyType: 'travel',
      productLine: 'International',
      destinationCountry: 'AE',
      departureDate: '',
      flyFrom: 'Islamabad',
      packageType: '5 Star',
      adults: 1,
      children: 0,
      infants: 0,
      includeFlight: true,
      includeHotel: true,
      includeTransport: true,
      includeHomePickup: false,
      includeVisa: false,
      includeCarRental: false,
      includeZiaraat: false,
      flightOrigin: 'ISB',
      flightDestination: 'DXB',
      selectedFlightOfferId: null,
      selectedFlightAmount: null,
      selectedFlightCurrency: null,
      selectedFlightLabel: null,
      visaType: 'Tourist',
      carRentalCity: '',
      carRentalDays: 3,
      carRentalClass: 'Sedan',
      firstEntryLocation: 'Dubai',
      firstEntryNights: 4,
      secondEntryLocation: 'Istanbul',
      secondEntryNights: 3,
      thirdEntryLocation: 'Antalya',
      thirdEntryNights: 2,
      includeFirstEntry: true,
      includeSecondEntry: false,
      includeThirdEntry: false,
      transportationType: 'Private Car',
      makkahZiaraat: 'None',
      madinaZiaraat: 'None',
      onlineSupport: true,
      fullName: '',
      phoneNumber: '',
      cityName: '',
      emailAddress: '',
      comments: '',
    };
    try {
      const raw = sessionStorage.getItem('customizeBookingDraft');
      if (raw) {
        const draft = JSON.parse(raw);
        const merged = { ...defaults, ...draft };
        const line = resolveProductLine(merged);
        return {
          ...merged,
          productLine: line,
          journeyType: line === 'Umrah' ? 'umrah' : 'travel',
          ...coerceBookingLocations(line, merged),
        };
      }
    } catch {
      /* ignore */
    }
    return defaults;
  });

  const updateBookingData = (updates) => {
    setBookingData((prev) => {
      let next = { ...prev, ...updates };
      if (updates.productLine != null || updates.destinationCountry != null || updates.journeyType != null) {
        const line = resolveProductLine(next);
        next = {
          ...next,
          productLine: line,
          journeyType: line === 'Umrah' ? 'umrah' : 'travel',
          ...coerceBookingLocations(line, next),
        };
      }
      try {
        sessionStorage.setItem('customizeBookingDraft', JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    try {
      sessionStorage.setItem('customizeBookingStep', String(activeStep));
    } catch {
      /* ignore */
    }
  }, [activeStep]);

  // Calculate price when moving to summary step (last step index 4)
  useEffect(() => {
    if (activeStep === 4) {
      calculatePrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  const calculatePrice = async () => {
    try {
      const { calculateCustomizedPrice } = await import('../../utils/customizedPricingAPI');
      const calculation = await calculateCustomizedPrice(bookingData);
      setPriceCalculation(calculation);
    } catch (error) {
      console.error('Price calculation error:', error);
      setSubmitError('Error calculating price. Please try again.');
    }
  };

  const handleNext = () => {
    setSubmitError('');

    // Validate current step
    if (activeStep === 0) {
      if (!bookingData.departureDate) {
        setSubmitError('Please select a departure date');
        return;
      }
      const selectedDate = new Date(bookingData.departureDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        setSubmitError('Departure date cannot be in the past');
        return;
      }
    }

    if (activeStep === 1) {
      const anyService =
        bookingData.includeFlight ||
        bookingData.includeHotel !== false ||
        bookingData.includeTransport ||
        bookingData.includeVisa ||
        bookingData.includeCarRental ||
        bookingData.includeZiaraat;
      if (!anyService) {
        setSubmitError('Please select at least one service');
        return;
      }
    }

    if (activeStep === 2) {
      if (bookingData.includeHotel !== false) {
        const totalNights =
          (bookingData.includeFirstEntry ? bookingData.firstEntryNights : 0) +
          (bookingData.includeSecondEntry ? bookingData.secondEntryNights : 0) +
          (bookingData.includeThirdEntry ? bookingData.thirdEntryNights : 0);

        if (totalNights <= 0) {
          setSubmitError('Please select at least one stay location with nights');
          return;
        }
      }
    }

    if (activeStep === 3) {
      if (!bookingData.fullName || !bookingData.phoneNumber || !bookingData.cityName) {
        setSubmitError('Please fill in all required contact information');
        return;
      }
      if (!/^\+?\d{10,15}$/.test(bookingData.phoneNumber.replace(/[\s\-().]/g, ''))) {
        setSubmitError('Please enter a valid phone number');
        return;
      }
      if (bookingData.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.emailAddress)) {
        setSubmitError('Please enter a valid email address');
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setSubmitError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleLoginRedirect = () => {
    setShowAuthModal(false);
    navigate('/v2/login', { state: { returnTo: '/v2/customized-booking' } });
  };

  const handleSignupRedirect = () => {
    setShowAuthModal(false);
    navigate('/v2/register');
  };

  const handleSubmitBooking = async () => {
    const token =
      localStorage.getItem('accessToken') ||
      localStorage.getItem('userToken') ||
      user?.token ||
      user?.accessToken;

    if (!token && !isAuthenticated) {
      setPendingBookingData(bookingData);
      setShowAuthModal(true);
      return;
    }

    await submitBooking(bookingData, token);
  };

  const submitBooking = async (data, authToken) => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      if (!authToken) {
        setSubmitError('Please sign in to submit your booking.');
        setShowAuthModal(true);
        return;
      }

      const axios = (await import('axios')).default;

      // Ensure we have a fresh price snapshot when possible
      let calc = priceCalculation;
      if (!calc || typeof calc.totalPrice !== 'number') {
        try {
          const { calculateCustomizedPrice } = await import('../../utils/customizedPricingAPI');
          calc = await calculateCustomizedPrice(data);
          setPriceCalculation(calc);
        } catch (priceErr) {
          console.warn('Price recalculation failed; submitting with available snapshot', priceErr);
        }
      }

      const bookingPayload = buildCustomizedBookingPayload(data, calc, {
        isGuestBooking: false,
      });

      const response = await axios.post(`${API_BASE_URL}/bookings`, bookingPayload, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data) {
        try {
          sessionStorage.removeItem('customizeBookingDraft');
          sessionStorage.removeItem('customizeBookingStep');
        } catch {
          /* ignore */
        }

        navigate(`/v2/booking-confirmation/${response.data.id || response.data.bookingId}`, {
          state: {
            bookingRef: response.data.bookingReference || response.data.bookingRef,
            pin: response.data.randomPin || response.data.pin,
            isCustomized: true,
            totalAmount: response.data.totalAmount,
          },
        });
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      const dataErr = error.response?.data;
      const validation =
        dataErr?.errors && typeof dataErr.errors === 'object'
          ? Object.values(dataErr.errors).flat().join('. ')
          : null;
      setSubmitError(
        dataErr?.message ||
          validation ||
          (!error.response
            ? 'Cannot reach the API. Start backend services (port 5000) and try again.'
            : null) ||
          'Failed to submit booking. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateGuestAccount = async () => {
    if (!guestPassword || guestPassword !== guestPasswordConfirm) {
      setSubmitError('Passwords do not match');
      return;
    }

    if (guestPassword.length < 8) {
      setSubmitError('Password must be at least 8 characters');
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(guestPassword)) {
      setSubmitError('Password must include uppercase, lowercase, and a number');
      return;
    }

    if (!bookingData.emailAddress) {
      setSubmitError('Email is required to create a guest account');
      return;
    }

    setGuestAccountLoading(true);
    setSubmitError('');

    try {
      const authService = (await import('../../services/auth/authService')).default;
      const names = (bookingData.fullName || 'Guest').trim().split(/\s+/);

      // register-guest creates the user but does not return a JWT
      await authService.registerGuest(
        bookingData.emailAddress,
        guestPassword,
        names[0],
        names.slice(1).join(' ') || 'Guest',
        bookingData.phoneNumber
      );

      // Log in immediately so booking (Authorize) can proceed
      const loginResult = await authService.login(bookingData.emailAddress, guestPassword);
      const token = loginResult.accessToken || loginResult.token || loginResult.AccessToken;
      if (!token) {
        setSubmitError('Account created, but sign-in failed. Please sign in and try again.');
        return;
      }

      dispatch(
        persistSession({
          accessToken: token,
          refreshToken: loginResult.refreshToken || loginResult.RefreshToken,
          user: {
            email: bookingData.emailAddress,
            firstName: names[0],
            lastName: names.slice(1).join(' ') || 'Guest',
          },
        })
      );

      setShowGuestAccountForm(false);
      setShowAuthModal(false);
      await submitBooking(pendingBookingData, token);
    } catch (error) {
      console.error('Guest account creation error:', error);
      const data = error.response?.data;
      const validationErrors = Array.isArray(data?.errors)
        ? data.errors.join('. ')
        : null;
      setSubmitError(
        data?.message
          || validationErrors
          || (!error.response
            ? 'Cannot reach the API. Start backend services (port 5000) and try again.'
            : null)
          || 'Failed to create guest account. Please try again.'
      );
    } finally {
      setGuestAccountLoading(false);
    }
  };

  const renderStepContent = () => {
    const stepProps = {
      bookingData,
      updateBookingData,
    };

    switch (activeStep) {
      case 0:
        return <BasicInformationStepV2 {...stepProps} />;
      case 1:
        return <ServicesStepV2 {...stepProps} />;
      case 2:
        return bookingData.includeHotel === false ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              Hotel / stay skipped
            </Typography>
            <Typography color="text.secondary">
              You turned off Hotel in Services. Click Next to continue with contact details.
            </Typography>
          </Box>
        ) : (
          <StayDurationStepV2 {...stepProps} />
        );
      case 3:
        return <TransportationContactStepV2 {...stepProps} />;
      case 4:
        return <PriceSummaryStepV2 {...stepProps} priceCalculation={priceCalculation} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      <HeaderV2 />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #003B95 0%, #0288D1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Customize Your Trip
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Pick only the services you need — flights, hotel, visa, transport, car rental, and more
            </Typography>
          </Box>
        </motion.div>

        {/* Stepper */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
          }}
        >
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      '&.Mui-active': {
                        color: 'primary.main',
                      },
                      '&.Mui-completed': {
                        color: 'success.main',
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Error Alert */}
        <AnimatePresence>
          {submitError && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError('')}>
                {submitError}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step Content */}
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.4 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
              mb: 4,
            }}
          >
            {renderStepContent()}
          </Paper>
        </motion.div>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<ArrowBack />}
            onClick={handleBack}
            disabled={activeStep === 0 || isSubmitting}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
            }}
          >
            Back
          </Button>

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              size="large"
              endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Check />}
              onClick={handleSubmitBooking}
              disabled={isSubmitting}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #003B95 0%, #0288D1 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #002D75 0%, #0277BD 100%)',
                },
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={handleNext}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #003B95 0%, #0288D1 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #002D75 0%, #0277BD 100%)',
                },
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </Container>

      {/* Auth Modal - Show when not logged in */}
      <Dialog open={showAuthModal} onClose={() => setShowAuthModal(false)} maxWidth="sm" fullWidth>
        {!showGuestAccountForm ? (
          <>
            <DialogTitle sx={{ fontWeight: 700, textAlign: 'center', fontSize: '1.3rem' }}>
              Do you have an account?
            </DialogTitle>
            <DialogContent sx={{ pt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Sign in to complete your booking, or create a guest account to get a booking reference and PIN.
              </Typography>
              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
                  {submitError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                onClick={handleLoginRedirect}
                variant="outlined"
                size="large"
                sx={{ minWidth: '140px' }}
              >
                Login
              </Button>
              <Button
                onClick={handleSignupRedirect}
                variant="outlined"
                size="large"
                sx={{ minWidth: '140px' }}
              >
                Sign Up
              </Button>
              <Button
                onClick={() => setShowGuestAccountForm(true)}
                variant="contained"
                size="large"
                sx={{ minWidth: '180px' }}
              >
                Guest Account
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>Create Guest Account</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose a password (min 8 chars, with uppercase, lowercase, and a number). You&apos;ll get a booking reference and PIN after booking.
              </Typography>
              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
                  {submitError}
                </Alert>
              )}
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={guestPassword}
                onChange={(e) => setGuestPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={guestPasswordConfirm}
                onChange={(e) => setGuestPasswordConfirm(e.target.value)}
                placeholder="Re-enter password"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowGuestAccountForm(false)} disabled={guestAccountLoading}>
                Back
              </Button>
              <Button
                onClick={handleCreateGuestAccount}
                variant="contained"
                disabled={guestAccountLoading}
              >
                {guestAccountLoading ? <CircularProgress size={24} /> : 'Create Account & Book'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <FooterV2 />
    </Box>
  );
};

export default CustomizedBookingPageV2;
